/**
 * Centralized URL builders.
 *
 * Two URL families:
 *   - /projects/:uuid/:step/:substep      — launch workflow (brainstorm, website)
 *   - /campaigns/:uuid/:substep           — campaign workflow, flat substep
 *
 * parseProjectUrl understands both and emits a normalized
 * { uuid, step, substep, workflowType } shape so a single workflow store can
 * handle either. For campaign URLs step = "ads" (synthetic, maps to the
 * legacy launch.ads subtree for navigation compatibility).
 */

export function projectUrl(uuid: string, ...segments: string[]): string {
  return ["/projects", uuid, ...segments].join("/");
}

/**
 * The stable thread home for a conversational surface (brainstorm, website).
 * The chat's thread_id is the address; the server resolves it to the right page.
 */
export function threadUrl(threadId: string): string {
  return `/threads/${threadId}`;
}

export function campaignUrl(uuid: string, ...segments: string[]): string {
  return ["/campaigns", uuid, ...segments].join("/");
}

export function parseProjectUrl(
  path: string
): { uuid: string; step: string; substep?: string; workflowType?: "launch" | "campaign" } | null {
  const projectMatch = path.match(/^\/projects\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/);
  if (projectMatch) {
    return {
      uuid: projectMatch[1]!,
      step: projectMatch[2]!,
      substep: projectMatch[3] || undefined,
      workflowType: "launch",
    };
  }

  // Campaign URLs: /campaigns/:uuid/:substep — campaign UUID addresses the
  // campaign directly; "ads" is synthesized as the step so existing
  // WorkflowPages / PAGE_ORDER machinery keeps resolving correctly.
  const campaignMatch = path.match(/^\/campaigns\/([^/]+)\/([^/]+)$/);
  if (campaignMatch) {
    return {
      uuid: campaignMatch[1]!,
      step: "ads",
      substep: campaignMatch[2]!,
      workflowType: "campaign",
    };
  }

  return null;
}

export function parseCampaignUrl(
  path: string
): { uuid: string; substep: string } | null {
  const match = path.match(/^\/campaigns\/([^/]+)\/([^/]+)$/);
  if (!match) return null;
  return { uuid: match[1]!, substep: match[2]! };
}
